import { createAsyncThunk, createSlice, miniSerializeError, SerializedError } from '@reduxjs/toolkit';
import type { RootState } from 'src/store';
import { resetStore } from './storeSlice';

import { ServerErrorAdapter } from '@itm/shared-frontend/lib/utils';

import { getTemplateSummaryByTenant, getTemplateSummaryByCompany } from 'src/api/notification/templates';
import { getTemplateSummaryDefault } from 'src/api/notification/systemTemplate';
import { getTenantCompaniesProductsCount } from 'src/api/clientPortal/tenant';
import { getAllTenantsCompaniesProductsCount } from 'src/api/clientPortal/company';

import {
  SystemTemplateModuleDto,
  TenantTemplateModuleDto,
  CompanyTemplateModuleDto,
  CompaniesProductsCountDto,
  ServerError,
} from 'src/types';

type RequestState<T> = {
  id: string;
  data: T;
  isLoading: boolean;
  serverErrors: string[];
};

type SummaryState<T> = RequestState<T[]>;

type AsyncThunkPayload<T extends RequestState<unknown>> = Pick<T, 'id' | 'data'>;

type DefaultTemplateSummaryState = Omit<SummaryState<SystemTemplateModuleDto>, 'id'>;
type DefaultCompaniesProductsCountState = Omit<RequestState<CompaniesProductsCountDto>, 'id'>;
type TenantTemplateSummaryState = SummaryState<TenantTemplateModuleDto>;
type TenantCompaniesProductsCountState = RequestState<CompaniesProductsCountDto>;
type CompanyTemplateSummaryState = SummaryState<CompanyTemplateModuleDto>;

type NotificationsState = {
  defaultSummary: DefaultTemplateSummaryState;
  defaultCompaniesProductsCount: DefaultCompaniesProductsCountState;
  tenantSummary: TenantTemplateSummaryState;
  companySummary: CompanyTemplateSummaryState;
  tenantCompaniesProductsCount: TenantCompaniesProductsCountState;
};

type FetchDefaultNotificationSummaryActionParams = {
  // skip the request when the summary is already in the store
  reuseExisting?: boolean;
};

type FetchDefaultCompaniesProductsCountActionParams = {
  // skip the request when the counts are already in the store
  reuseExisting?: boolean;
};

type FetchTenantNotificationSummaryActionParams = {
  tenantId: string;
  // skip the request when the summary is already in the store for this tenant
  reuseExisting?: boolean;
};

type FetchCompanyNotificationSummaryActionParams = {
  companyId: string;
  // skip the request when the summary is already in the store for this company
  reuseExisting?: boolean;
};

type FetchTenantCompaniesProductsCountActionParams = {
  tenantId: string;
  // skip the request when the counts are already in the store for this tenant
  reuseExisting?: boolean;
};

const serializeError = (e: unknown): SerializedError => {
  const message = new ServerErrorAdapter(e as ServerError).combine().join('\n  ');
  return miniSerializeError({ message });
};

export const fetchDefaultNotificationSummaryAction = createAsyncThunk<
  SystemTemplateModuleDto[],
  FetchDefaultNotificationSummaryActionParams | undefined,
  { state: RootState }
>(
  'notifications/fetchDefaultNotificationSummaryAction',
  async (_params, { signal }) => {
    const res = await getTemplateSummaryDefault({ signal });
    return res.data;
  },
  {
    serializeError,
    condition: ({ reuseExisting } = {}, { getState }) =>
      !reuseExisting || !getState().notifications.defaultSummary.data.length,
  },
);

export const fetchDefaultCompaniesProductsCountAction = createAsyncThunk<
  CompaniesProductsCountDto,
  FetchDefaultCompaniesProductsCountActionParams | undefined,
  { state: RootState }
>(
  'notifications/fetchDefaultCompaniesProductsCountAction',
  async (_params, { signal }) => {
    const res = await getAllTenantsCompaniesProductsCount({ signal });
    return res.data;
  },
  {
    serializeError,
    condition: ({ reuseExisting } = {}, { getState }) =>
      !reuseExisting || !Object.keys(getState().notifications.defaultCompaniesProductsCount.data).length,
  },
);

export const fetchTenantNotificationSummaryAction = createAsyncThunk<
  AsyncThunkPayload<TenantTemplateSummaryState>,
  FetchTenantNotificationSummaryActionParams,
  { state: RootState }
>(
  'notifications/fetchTenantNotificationSummaryAction',
  async ({ tenantId }, { signal }) => {
    const res = await getTemplateSummaryByTenant(tenantId, { signal });
    return { id: tenantId, data: res.data };
  },
  {
    serializeError,
    condition: ({ tenantId, reuseExisting }, { getState }) =>
      !reuseExisting || getState().notifications.tenantSummary.id !== tenantId,
  },
);

export const fetchTenantCompaniesProductsCountAction = createAsyncThunk<
  AsyncThunkPayload<TenantCompaniesProductsCountState>,
  FetchTenantCompaniesProductsCountActionParams,
  { state: RootState }
>(
  'notifications/fetchTenantCompaniesProductsCountAction',
  async ({ tenantId }, { signal }) => {
    const res = await getTenantCompaniesProductsCount(tenantId, { signal });

    return { id: tenantId, data: res.data };
  },
  {
    serializeError,
    condition: ({ tenantId, reuseExisting }, { getState }) =>
      !reuseExisting || getState().notifications.tenantCompaniesProductsCount.id !== tenantId,
  },
);

export const fetchCompanyNotificationSummaryAction = createAsyncThunk<
  AsyncThunkPayload<CompanyTemplateSummaryState>,
  FetchCompanyNotificationSummaryActionParams,
  { state: RootState }
>(
  'notifications/fetchCompanyNotificationSummaryAction',
  async ({ companyId }, { signal }) => {
    const res = await getTemplateSummaryByCompany(companyId, { signal });
    return { id: companyId, data: res.data };
  },
  {
    serializeError,
    condition: ({ companyId, reuseExisting }, { getState }) =>
      !reuseExisting || getState().notifications.companySummary.id !== companyId,
  },
);

const initialState: NotificationsState = {
  defaultSummary: { data: [], isLoading: false, serverErrors: [] },
  defaultCompaniesProductsCount: { data: {}, isLoading: false, serverErrors: [] },
  tenantSummary: { id: '', data: [], isLoading: false, serverErrors: [] },
  companySummary: { id: '', data: [], isLoading: false, serverErrors: [] },
  tenantCompaniesProductsCount: { id: '', data: {}, isLoading: false, serverErrors: [] },
};

const notificationTemplateSummarySlice = createSlice({
  name: 'notificationTemplateSummary',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // Default
    builder.addCase(fetchDefaultNotificationSummaryAction.pending, (state: NotificationsState) => {
      state.defaultSummary = { ...initialState.defaultSummary, isLoading: true };
    });
    builder.addCase(fetchDefaultNotificationSummaryAction.fulfilled, (state: NotificationsState, action) => {
      state.defaultSummary.isLoading = false;
      state.defaultSummary.data = action.payload;
    });
    builder.addCase(fetchDefaultNotificationSummaryAction.rejected, (state: NotificationsState, action) => {
      if (action.meta.aborted) return;
      state.defaultSummary.isLoading = false;
      state.defaultSummary.serverErrors = action.error.message ? [action.error.message] : [];
    });

    // Default companies-products count
    builder.addCase(fetchDefaultCompaniesProductsCountAction.pending, (state: NotificationsState) => {
      state.defaultCompaniesProductsCount = { ...initialState.defaultCompaniesProductsCount, isLoading: true };
    });
    builder.addCase(fetchDefaultCompaniesProductsCountAction.fulfilled, (state: NotificationsState, action) => {
      state.defaultCompaniesProductsCount.isLoading = false;
      state.defaultCompaniesProductsCount.data = action.payload;
    });
    builder.addCase(fetchDefaultCompaniesProductsCountAction.rejected, (state: NotificationsState, action) => {
      if (action.meta.aborted) return;
      state.defaultCompaniesProductsCount.isLoading = false;
      state.defaultCompaniesProductsCount.serverErrors = action.error.message ? [action.error.message] : [];
    });

    // Tenant
    builder.addCase(fetchTenantNotificationSummaryAction.pending, (state: NotificationsState) => {
      state.tenantSummary = { ...initialState.tenantSummary, isLoading: true };
    });
    builder.addCase(fetchTenantNotificationSummaryAction.fulfilled, (state: NotificationsState, action) => {
      state.tenantSummary.isLoading = false;
      state.tenantSummary.id = action.payload.id;
      state.tenantSummary.data = action.payload.data;
    });
    builder.addCase(fetchTenantNotificationSummaryAction.rejected, (state: NotificationsState, action) => {
      if (action.meta.aborted) return;
      state.tenantSummary.isLoading = false;
      state.tenantSummary.serverErrors = action.error.message ? [action.error.message] : [];
    });

    // Tenant companies-products count
    builder.addCase(fetchTenantCompaniesProductsCountAction.pending, (state: NotificationsState) => {
      state.tenantCompaniesProductsCount = { ...initialState.tenantCompaniesProductsCount, isLoading: true };
    });
    builder.addCase(fetchTenantCompaniesProductsCountAction.fulfilled, (state: NotificationsState, action) => {
      state.tenantCompaniesProductsCount.isLoading = false;
      state.tenantCompaniesProductsCount.id = action.payload.id;
      state.tenantCompaniesProductsCount.data = action.payload.data;
    });
    builder.addCase(fetchTenantCompaniesProductsCountAction.rejected, (state: NotificationsState, action) => {
      if (action.meta.aborted) return;
      state.tenantCompaniesProductsCount.isLoading = false;
      state.tenantCompaniesProductsCount.serverErrors = action.error.message ? [action.error.message] : [];
    });

    // Company
    builder.addCase(fetchCompanyNotificationSummaryAction.pending, (state: NotificationsState) => {
      state.companySummary = { ...initialState.companySummary, isLoading: true };
    });
    builder.addCase(fetchCompanyNotificationSummaryAction.fulfilled, (state: NotificationsState, action) => {
      state.companySummary.isLoading = false;
      state.companySummary.id = action.payload.id;
      state.companySummary.data = action.payload.data;
    });
    builder.addCase(fetchCompanyNotificationSummaryAction.rejected, (state: NotificationsState, action) => {
      if (action.meta.aborted) return;
      state.companySummary.isLoading = false;
      state.companySummary.serverErrors = action.error.message ? [action.error.message] : [];
    });
    // Cleanup
    builder.addCase(resetStore, (state: NotificationsState) => {
      Object.assign(state, initialState);
    });
  },
});

export default notificationTemplateSummarySlice.reducer;
